import React, { useCallback, useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useRowLinkBuilder } from '../../../lib/useRowLink';
import type { ColumnDef, StockFeatures } from '@tanstack/react-table';
import {
  Page as PageLayout,
  DataTable,
  LoadingOrbit,
  Text,
  Button,
  useToast,
  DialogTrigger,
  Dialog,
  DialogContent,
  DialogFooter,
  Select,
  TagBadge,
  badgeVariantToTagColor,
} from '@chatsdet/ui';
import { Play } from '@phosphor-icons/react';
import { useQueryClient } from '@tanstack/react-query';
import { useBreadcrumbs } from '../../../contexts/BreadcrumbsContext';
import { useOrgQaRuns, useStartQaRun } from '../../../features/qa-runs/useQaRuns';
import type { QaRunListItem, QaRunStatus } from '../../../features/qa-runs/qaRunTypes';
import { getQaRunStatusMeta } from '../../../features/qa-runs/qaRunTypes';
import { useOrgPropertySelection } from '../../../hooks/useOrgPropertySelection';
import { CredentialMultiSelect } from '../../components/CredentialMultiSelect';

const StatusBadge: React.FC<{ status: QaRunStatus }> = ({ status }) => {
  const meta = getQaRunStatusMeta(status);
  const colorMap: Record<string, 'success' | 'secondary' | 'warning' | 'destructive' | 'outline'> =
    {
      green: 'success',
      blue: 'secondary',
      yellow: 'warning',
      red: 'destructive',
      gray: 'outline',
    };
  return (
    <TagBadge
      name={meta.label}
      color={badgeVariantToTagColor(colorMap[meta.color] ?? 'outline')}
      hideDot
    />
  );
};

const formatDuration = (
  startedAt: string | null | undefined,
  finishedAt: string | null | undefined
): string => {
  if (!startedAt) return '-';
  const start = new Date(startedAt);
  const end = finishedAt ? new Date(finishedAt) : new Date();
  const durationMs = end.getTime() - start.getTime();
  if (durationMs < 1000) return '<1s';
  if (durationMs < 60000) return `${Math.round(durationMs / 1000)}s`;
  if (durationMs < 3600000) return `${Math.round(durationMs / 60000)}m`;
  return `${Math.round(durationMs / 3600000)}h`;
};

export function QaRunsPage() {
  const navigate = useNavigate();
  const buildHref = useRowLinkBuilder();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const searchParams = useSearch({ from: '/runs/qa' }) as { page: number; pageSize: number };
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedPropertyId, setSelectedPropertyId] = useState<string | null>(null);
  const [selectedCredentialIds, setSelectedCredentialIds] = useState<string[]>([]);

  useBreadcrumbs([
    { key: 'runs', label: 'Runs', href: '/runs' },
    { key: 'qa', label: 'QA' },
  ]);

  const qaRunsQuery = useOrgQaRuns({ page: searchParams.page, pageSize: searchParams.pageSize });
  const { propertyOptions } = useOrgPropertySelection();
  const startRunMutation = useStartQaRun(selectedPropertyId ?? undefined);

  const handlePageChange = useCallback(
    (page: number) => {
      navigate({
        to: '/runs/qa',
        search: (prev: Record<string, unknown>) => {
          const next: Record<string, unknown> = { ...prev, page };
          if (page === 1) delete next.page;
          return next;
        },
      });
    },
    [navigate]
  );

  const handlePageSizeChange = useCallback(
    (pageSize: number) => {
      navigate({
        to: '/runs/qa',
        search: (prev: Record<string, unknown>) => ({ ...prev, page: 1, pageSize }),
      });
    },
    [navigate]
  );

  const handleStartQaRun = async () => {
    if (!selectedPropertyId) {
      toast.info('Please select an application');
      return;
    }
    try {
      const credentialIds = selectedCredentialIds;
      const result = await startRunMutation.mutateAsync({
        credentialIds,
      });
      setDialogOpen(false);
      setSelectedPropertyId(null);
      setSelectedCredentialIds([]);
      queryClient.invalidateQueries({ queryKey: ['qa-runs', 'org'] });
      if (result.pageTestCount === 0) {
        toast.info(
          credentialIds.length > 0
            ? 'No pages seen by the selected credentials in the last 30 days'
            : 'No pages found in the last 30 days to test'
        );
      } else {
        const scope = credentialIds.length > 0 ? ' for the selected credentials' : '';
        toast.success(
          `Started QA run testing ${result.pageTestCount} page${result.pageTestCount === 1 ? '' : 's'}${scope}`
        );
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to start QA run');
    }
  };

  const newRunButton = (size: 'sm' | 'md') => (
    <Button
      size={size}
      variant="primary"
      iconLeft={<Play size={size === 'sm' ? 14 : 16} weight="fill" />}
      isDisabled={startRunMutation.isPending}
      onPress={() => setDialogOpen(true)}
    >
      New QA Run
    </Button>
  );

  const columns: ColumnDef<StockFeatures, QaRunListItem>[] = [
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => <StatusBadge status={row.original.status} />,
      meta: { flex: 1 },
    },
    {
      accessorKey: 'propertyName',
      header: 'Application',
      cell: ({ row }) => (
        <Text size="sm" className="truncate">
          {row.original.propertyName}
        </Text>
      ),
      meta: { flex: 1.5 },
    },
    {
      accessorKey: 'createdAt',
      header: 'Started',
      cell: ({ row }) => (
        <Text size="sm" className="text-muted-foreground">
          {row.original.startedAt
            ? new Date(row.original.startedAt).toLocaleString()
            : new Date(row.original.createdAt).toLocaleString()}
        </Text>
      ),
      meta: { flex: 2 },
    },
    {
      id: 'duration',
      header: 'Duration',
      cell: ({ row }) => (
        <Text size="sm" className="text-muted-foreground">
          {formatDuration(row.original.startedAt, row.original.finishedAt)}
        </Text>
      ),
      meta: { flex: 1 },
    },
    {
      accessorKey: 'pagesTested',
      header: 'Pages Tested',
      cell: ({ row }) => (
        <Text size="sm" className="text-muted-foreground">
          {row.original.pagesTested ?? 0}
        </Text>
      ),
      meta: { flex: 1 },
    },
    {
      accessorKey: 'casesTested',
      header: 'Cases Tested',
      cell: ({ row }) => (
        <Text size="sm" className="text-muted-foreground">
          {row.original.casesTested ?? 0}
        </Text>
      ),
      meta: { flex: 1 },
    },
    {
      accessorKey: 'healthyTests',
      header: 'Healthy',
      cell: ({ row }) => (
        <Text size="sm" className="text-muted-foreground">
          {row.original.healthyTests ?? 0}
        </Text>
      ),
      meta: { flex: 1 },
    },
    {
      accessorKey: 'unhealthyTests',
      header: 'Unhealthy',
      cell: ({ row }) => (
        <Text size="sm" className="text-muted-foreground">
          {row.original.unhealthyTests ?? 0}
        </Text>
      ),
      meta: { flex: 1 },
    },
  ];

  return (
    <PageLayout>
      {qaRunsQuery.isLoading ? (
        <div className="flex items-center justify-center py-12">
          <LoadingOrbit size="lg" />
        </div>
      ) : qaRunsQuery.data && qaRunsQuery.data.runs.length > 0 ? (
        <>
          <div className="flex justify-end mb-4">{newRunButton('md')}</div>
          <DataTable
            columns={columns}
            data={qaRunsQuery.data.runs}
            aria-label="QA runs table"
            serverPagination={{
              page: searchParams.page,
              pageSize: searchParams.pageSize,
              pageCount: qaRunsQuery.data.pagination.totalPages,
              totalItems: qaRunsQuery.data.pagination.totalItems,
              onPageChange: handlePageChange,
              onPageSizeChange: handlePageSizeChange,
            }}
            getRowHref={(run) =>
              buildHref({
                to: '/runs/$propertyId/$runId',
                params: { propertyId: run.propertyId, runId: run.id },
              })
            }
          />
        </>
      ) : (
        <div className="text-center py-12 border-2 border-dashed border-border rounded-lg">
          <Play size={48} className="mx-auto text-muted-foreground mb-4" />
          <Text className="text-muted-foreground">No QA runs yet</Text>
          <Text size="sm" className="text-muted-foreground mt-1">
            Start your first QA run to test pages discovered in the last 30 days
          </Text>
          <div className="mt-4 flex justify-center">{newRunButton('sm')}</div>
        </div>
      )}

      <DialogTrigger isOpen={dialogOpen} onOpenChange={setDialogOpen} size="sm">
        <span style={{ display: 'none' }} />
        <Dialog title="Start QA Run" description="Select a application to run QA tests on">
          <DialogContent>
            <Select
              label="Application"
              placeholder="Select a application"
              items={propertyOptions.map((opt) => ({ id: opt.id, label: opt.label }))}
              selectedKey={selectedPropertyId}
              onSelectionChange={(key) => {
                setSelectedPropertyId(key as string);
                // Credentials are property-scoped — clear the prior selection on change.
                setSelectedCredentialIds([]);
              }}
            />
            {selectedPropertyId ? (
              <div className="mt-4">
                <Text size="sm" className="font-medium text-foreground">
                  Credentials (optional)
                </Text>
                <Text size="xs" className="text-muted-foreground mb-2">
                  Only test pages seen by the selected credentials, each under its own login. Leave
                  empty to test all pages from the last 30 days.
                </Text>
                <CredentialMultiSelect
                  propertyId={selectedPropertyId}
                  selectedIds={selectedCredentialIds}
                  onChange={setSelectedCredentialIds}
                  isDisabled={startRunMutation.isPending}
                />
              </div>
            ) : null}
          </DialogContent>
          <DialogFooter>
            <Button
              variant="outline"
              onPress={() => {
                setDialogOpen(false);
                setSelectedPropertyId(null);
                setSelectedCredentialIds([]);
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onPress={() => void handleStartQaRun()}
              isDisabled={!selectedPropertyId || startRunMutation.isPending}
            >
              {startRunMutation.isPending ? 'Starting...' : 'Start Run'}
            </Button>
          </DialogFooter>
        </Dialog>
      </DialogTrigger>
    </PageLayout>
  );
}
